import { IContactAddress } from '../address/address';
import { IPhotoEntityReference } from '../basic';
import {
  EntityReference,
  IArticleKeyEntityReference,
  IEntityReferenceWithDisplayName,
  IEntityReferenceWithNumber,
  IShippingMethodEntityReference
} from '../basic/basic';
import { IContact } from '../contact/contact';
import { ILanguage } from '../language/language';
import { IPayment } from '../payments/payment';
import { IResult } from '../results/result';
import { IAmount, ICurrency, ItemType, TransactionType } from '../structure';
import { ITaxRateEntityReference } from '../tax-rate/tax-rate';
import { IVoucherEntityReference } from '../vouchers/voucher';
import { IOrderTransaction, OrderTransactionStatusType } from './order-transaction';

/** Represents a document of a given order */
export interface IDocumentation {
  /** unique id for the documentation itself */
  documentationID: number;
  /** thumbnail picture of the invoice */
  thumbnail: IPhotoEntityReference;
  /** unique identifier for datafile */
  datafile: EntityReference;
  /** language of the order */
  language: ILanguage;
  /** title of the order documentation */
  title: string;
  /** documentationType, corresponding to enum DocumentationType */
  type: DocumentationType;
}

/** representing the orderstatus that are available */
export enum OrderStatus {
  NotEdited = 0,
  Confirmed = 1,
  Canceled = 2,
  Ready = 3,
  InProcess = 4,
  Replacement = 5
}

/** representing the type of the order */
export enum OrderType {
  ORDER = 0,
  INQUIRY = 1,
  DRAFT = 2,
  WINTERINGS = 3,
  EVENT = 4
}

/** representing the type of the documents that are available */
export enum DocumentationType {
  Custom = 0,
  Invoice = 1,
  Manual = 2,
  DeliverySlip = 3,
  PackingSlip = 4,
  RequestList = 5,
  DeliverySlipTransaction = 6,
  Offer = 7,
  Presentation = 8,
  PackingSlipTransaction = 9,
  PackingSlipOrders = 10,
  TransferOrder = 11,
  WatchList = 12,
  MarkupOrder = 13,
  DebitCardVerificationCodeLetter = 14,
  DocumentTemplate = 15,
  Download = 16,
  WatchListWithoutDiscounts = 17,
  Receipt = 18,
  ContainerReceipt = 19,
  Signage = 20,
  Refund = 21
}

/** representing the type of an invoice (Rechnung / Gutschrift / Storno Gutschrift) */
export enum InvoiceType {
  Invoice = 0,
  CreditNote = 1,
  CancellationCreditNote = 2
}

/** Reference to an invoice of an order, including its type */
export interface IInvoiceEntityReference extends EntityReference {
  /** invoiceType, corresponding to enum InvoiceType */
  type: InvoiceType;
}

/** represents a result entry of an order process */
export interface IOrderResult {
  /** unique id of the order */
  orderID: number;
  /** unique id of the order transaction */
  orderTransactionID: number;
}

/** represents a single item in an order */
export interface IOrderItem {
  /** unique id of the order item */
  orderItemID: number;
  /** Is this item a voucher? */
  isVoucher?: boolean;
  /** Is this item a ticket? */
  isTicket?: boolean;
  /**
   * May the signage document (ticket, voucher) of this item be downloaded?
   * False for a cancelled booking and on the refund/credit note row. Older backends omit it,
   * so a missing value keeps the previous behaviour and only an explicit false blocks.
   */
  canDownload?: boolean;
  /**
   * Is this item cancelled? Used to tell a cancelled booking apart from one whose document is
   * merely not ready yet — the latter shows "available soon", which would be wrong for a
   * cancellation because no document is ever coming.
   */
  isCancelled?: boolean;
  /** All contacts associated with this item (for vouchers, tickets, etc.) */
  contacts?: IContact[];
  articleGroups?: EntityReference[];
  date?: Date;
  deliveryDate?: Date;
  availableOn?: Date;
  alreadyRated?: boolean;
  fixedAvailableOn?: Date;
  info?: string;
  info2?: string;
  info3?: string;
  eAN?: string;
  photo?: IPhotoEntityReference;
  articleKey?: IArticleKeyEntityReference;
  article?: EntityReference;
  notes?: string;
  type?: ItemType;
  price?: number;
  fixedPrice?: number;
  priceOld?: number;
  packingUnit?: number;
  packingUnitFract?: number;
  pricePurchase?: number;
  totalPricePurchase?: number;
  /** AMAZON: Shipping price */
  fixedShippingPrice?: number;
  /** AMAZON: Discount on shipping */
  fixedShippingDiscount?: number;
  /** AMAZON: Promotion discount */
  fixedPromotionDiscount?: number;
  label?: boolean;
  locked?: boolean;
  priceLabel?: number;
  fixedPriceLicense?: number;
  stock?: boolean;
  dropShip?: boolean;
  currency: EntityReference;
  supplier?: EntityReference;
  quantity?: number;
  rated?: Date;
  ratedBy?: EntityReference;
  position?: number;
  totalPrice?: number;
  totalCosts?: number;
  taxCosts?: number;
  taxRate?: ITaxRateEntityReference;
  glyph?: string;
  transaction?: EntityReference;
  transactionShippingMethod: IShippingMethodEntityReference;
  transactionType?: TransactionType;
  vouchers?: IVoucherEntityReference[];
  signage?: string;
  results?: IResult[];
  isConfirmed?: boolean;
  confirmed?: Date;
  /** User who confirmed the order TODO: Interface IUser missing */
  confirmedBy?: unknown;
  quantityConfirmed?: number;
  external_CMS_ID?: number;
  customerEAN?: string;
  customerArticleNumber?: string;
  supplierValue?: string;
  customerPrice?: number;
  status?: OrderTransactionStatusType;
  quantityAvailable?: number;
  weight?: number;
  chainStore?: IEntityReferenceWithNumber;
  /** Custom fields TODO: Interface for ICustomField missing */
  customFields?: Array<unknown>;
  /** DetailLink for the corresponding ArticleKeyID */
  detailLink?: string;
}

/** represents the order object */
export interface IOrder {
  /** items in the order */
  items: IOrderItem[];
  /** order number */
  number: string;
  /** total costs of the order */
  totalCosts: number;
  /** Indicates if some of the items on the order can get downloaded/viewed (voucher, ticket) */
  hasDownload: boolean;
  /** date of when the order was paid */
  paydOn: Date;
  /** order ID */
  orderID: number;
  /** payment method */
  paymentMethod: {
    /** name of the payment method */
    displayName: string;
    /** id of the payment method */
    iD: number;
  };
  /** documents of the order */
  documentations: IDocumentation[];
  /** name of the order owner */
  ownerName?: string;
  /** indicates if the owner is a guest */
  ownerGuest?: boolean;
  /** voucher */
  voucher?: {
    /** unique id for the voucher */
    iD: number;
    amount: number;
  };
  /** code for the voucher */
  voucherCode?: {
    /** unique id for the voucher code */
    iD: number;
  };
  /** reference to the member who made the order */
  invoiceAddress?: IContactAddress;
  /** shipping address for the order */
  shippingAddress?: IContactAddress;
  /** status of the order */
  status?: OrderStatus;
  /** type of the order */
  type?: OrderType;
  /** timestamp when the status was set */
  statusOn: Date;
  /** timestamp of the most recent change */
  recentOn: Date;
  /** indicates if tax is added to prices */
  taxPlus: boolean;
  /** total weight of the order */
  totalWeight: number;
  /** total costs of articles only */
  totalCostsArticles: number;
  /** total tax costs for group 1 */
  totalTaxCosts1: number;
  /** tax rate for group 1 */
  taxRate1: ITaxRateEntityReference;
  /** total tax costs for group 2 */
  totalTaxCosts2: number;
  /** tax rate for group 2 */
  taxRate2: ITaxRateEntityReference;
  /** total tax costs for group 3 */
  totalTaxCosts3: number;
  /** tax rate for group 3 */
  taxRate3: ITaxRateEntityReference;
  /** total tax costs for group 4 */
  totalTaxCosts4: number;
  /** tax rate for group 4 */
  taxRate4: ITaxRateEntityReference;
  /** indicates if the order is suspended */
  suspended: boolean;
  /** requested shipping date */
  wantedShippingDate: Date;
  /** loading date of the order */
  loadingDate: Date;
  /** total tax costs for delivery */
  totalTaxCostsDeliver: number;
  /** total costs for delivery */
  totalCostsDeliver: number;
  /** tax rate for delivery */
  taxRateDeliver: ITaxRateEntityReference;
  /** ignore calculation for group items */
  ignoreCalculationGroupItems: boolean;
  /** use purchase price for calculation */
  usePricePurchase: boolean;
  /** reference to marketplace account */
  marketPlaceAccount: EntityReference;
  /** sales channel reference */
  channel: EntityReference;
  /** total costs for shipping */
  totalCostsShipping: number;
  /** total tax costs for shipping */
  totalTaxCostsShipping: number;
  /** tax rate for shipping */
  taxRateShipping: ITaxRateEntityReference;
  /** external CMS endpoint reference */
  external_CMS_Endpoint: string;
  /** external CMS order number */
  external_CMS_Number: string;
  /** external data */
  external_Data: string;
  /** external CMS id */
  external_CMS_ID: number;
  /** flag to skip calculation */
  dontCalculate: boolean;
  /** total discount value */
  totalDiscount: number;
  /** total tax costs of discount */
  totalTaxCostsDiscount: number;
  /** discount amount */
  discount: number;
  /** total paid amount */
  totalPaid: number;
  /** tax rate of discount */
  taxRateDiscount: ITaxRateEntityReference;
  /** user who approved the order */
  approvedBy: EntityReference;
  /** approval date */
  approvedOn: Date;
  /** confirmation date */
  confirmedOn: Date;
  /** cancellation date */
  cancelledOn: Date;
  /** a cancellation was requested and is not answered yet */
  cancellationRequested?: boolean;
  /** the requested cancellation was confirmed */
  cancellationRequestAccepted?: boolean;
  /** the requested cancellation is no longer possible */
  cancellationRequestedNotAccepted?: boolean;
  /** a return was requested and is not answered yet */
  returnRequested?: boolean;
  /** the requested return was accepted */
  returnRequestAccepted?: boolean;
  /** the requested return was rejected */
  returnRequestedNotAccepted?: boolean;
  /** notes for the order */
  notes: string;
  /** indicates if no mails should be sent */
  noMails: boolean;
  /** supplier reference */
  supplier: EntityReference;
  /** file reference */
  file: EntityReference;
  /** currency of the order */
  currency: ICurrency;
  /** debit card reference */
  debitCard: EntityReference;
  /** transactions of the order */
  transactions: IOrderTransaction[];
  /** references to shipment orders */
  shipmentOrders: EntityReference[];
  /** references to invoices */
  invoices: IInvoiceEntityReference[];
  /** payment entries of the order */
  payments: IPayment[];
  /** shipping method reference */
  shippingMethod?: IEntityReferenceWithDisplayName;
  /** creation date of the order */
  createdOn: Date;
  /** result entries of the order */
  results: IOrderResult[];
  /** reference to pickup chain store */
  pickupChainStore: EntityReference;
  /** reference if merged into another order */
  mergedInto: EntityReference;
  /** confirmation message text */
  confirmationMessage: string;
  /** unique number of the owner */
  ownerNumber: number;
  /** fixed total shipping costs */
  fixedTotalCostsShipping: number;
  /** fixed total discount */
  fixedTotalDiscount: number;
  /** reference to basket */
  basket: EntityReference;
  /** transaction token string */
  transactionToken: string;
  /** payment token id */
  paymentToken: number;
  /** seller name */
  seller: string;
  /** cashier name */
  cashier: string;
  /** cash desk number */
  cashDescNumber: string;
  /** transaction reference string */
  transaction: string;
  /** receipt number of the order */
  receiptNumber: string;
  /** external order number */
  orderNumber: string;
  /** how many items in total */
  quantity?: IAmount;
  /** indicates if order is cached */
  cache?: boolean;
  /** owner id of the order shop */
  ownerMemberID?: number;
  /** link to order details page */
  detailLink?: string;
  /** The GUID of the order */
  guid?: string;
}
